/*
  Cart drawer recommendations.

  The drawer markup is re-rendered by Shopify on every cart update, so the <cart-recommendations>
  element re-fetches its own content each time it connects: the recommended products of the most
  recently added product whose list still has something to show, rendered by
  sections/cart-drawer-recommendations.liquid in that product's context.

  Adding a suggestion from the drawer therefore keeps the rest of the list visible: the product
  just added only takes over when its own list is not empty once unavailable products and products
  already in the cart are filtered out.

  With no add to cart in this tab (reload, new tab, returning visitor) the cart line items rendered
  in `data-cart-handles` take over as the source, most recently added first.
*/
(() => {
  if (window.CartRecommendations) return;

  const SECTION_ID = 'cart-drawer-recommendations';
  const STORAGE_KEY = 'beseed:cart-reco-history';
  const HISTORY_LIMIT = 5;

  // Keyed by `handle::cart fingerprint` so a re-render after a quantity change reuses the markup,
  // but a cart whose contents changed is re-fetched (the filtering happens server side).
  const markupCache = new Map();

  // Added product handles, most recent first.
  let history = null;

  function readHistory() {
    if (history) return history;

    try {
      const stored = JSON.parse(sessionStorage.getItem(STORAGE_KEY));
      history = Array.isArray(stored) ? stored.filter((handle) => typeof handle === 'string') : [];
    } catch (error) {
      history = [];
    }

    return history;
  }

  function recordAddedProduct(handle) {
    if (!handle) return;

    history = [handle, ...readHistory().filter((stored) => stored !== handle)].slice(0, HISTORY_LIMIT);

    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(history));
    } catch (error) {
      // Private browsing: the in-memory history still covers the current page.
    }
  }

  function handleFromUrl(url) {
    const match = typeof url === 'string' ? url.match(/\/products\/([^?#/]+)/) : null;
    return match ? match[1] : '';
  }

  // `cart/add.js` answers with the added line item; guard against the full cart object, whose
  // first item has nothing to do with what was just added.
  function handleFromAddResponse(data) {
    if (!data || typeof data !== 'object' || data.item_count !== undefined) return '';

    const item = Array.isArray(data.items) ? data.items[0] : null;

    return data.handle || handleFromUrl(data.url) || (item ? item.handle || handleFromUrl(item.url) : '') || '';
  }

  class CartRecommendations extends HTMLElement {
    connectedCallback() {
      // With nothing added in this tab we only have the cart to go by: hold the request back until
      // the drawer is actually opened, instead of firing one on every page view.
      if (readHistory().length || this.isDrawerOpen) {
        this.render();
      } else {
        this.renderWhenDrawerOpens();
      }
    }

    disconnectedCallback() {
      this.drawerObserver?.disconnect();
    }

    get drawer() {
      return this.closest('cart-drawer');
    }

    get isDrawerOpen() {
      return !this.drawer || this.drawer.classList.contains('active');
    }

    renderWhenDrawerOpens() {
      const drawer = this.drawer;

      this.drawerObserver = new MutationObserver(() => {
        if (!drawer.classList.contains('active')) return;
        this.drawerObserver.disconnect();
        this.render();
      });

      this.drawerObserver.observe(drawer, { attributes: true, attributeFilter: ['class'] });
    }

    // Most recently added first: what this tab saw being added and is still in the cart, then the
    // remaining line items. A product removed from the cart stops driving the suggestions.
    get candidates() {
      const fromCart = (this.dataset.cartHandles || '').split(',').filter(Boolean);

      if (!fromCart.length) return readHistory().slice(0, HISTORY_LIMIT);

      const added = readHistory().filter((handle) => fromCart.includes(handle));

      return [...new Set([...added, ...fromCart])].slice(0, HISTORY_LIMIT);
    }

    get cartFingerprint() {
      return this.dataset.cartHandles || '';
    }

    async render() {
      const fingerprint = this.cartFingerprint;

      for (const handle of this.candidates) {
        const markup = await this.fetchMarkup(handle, fingerprint);

        // The drawer was re-rendered while we were fetching: a newer element is in charge.
        if (!this.isConnected) return;

        if (markup) {
          this.innerHTML = markup;
          return;
        }
      }

      this.innerHTML = '';
    }

    // Resolves to the section markup, or an empty string when this product has nothing left to suggest.
    async fetchMarkup(handle, fingerprint) {
      const cacheKey = `${handle}::${fingerprint}`;

      if (markupCache.has(cacheKey)) return markupCache.get(cacheKey);

      try {
        const response = await fetch(
          `${window.Shopify.routes.root}products/${encodeURIComponent(handle)}?section_id=${SECTION_ID}`
        );

        if (!response.ok) return '';

        const section = new DOMParser()
          .parseFromString(await response.text(), 'text/html')
          .querySelector('.shopify-section');
        const markup = section && section.innerHTML.trim() ? section.innerHTML : '';

        markupCache.set(cacheKey, markup);

        return markup;
      } catch (error) {
        console.error(error);
        return '';
      }
    }
  }

  window.CartRecommendations = { recordAddedProduct };

  if (!customElements.get('cart-recommendations')) {
    customElements.define('cart-recommendations', CartRecommendations);
  }

  // Runs before the drawer is re-rendered, so the fresh history is in place when the new
  // <cart-recommendations> connects.
  subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
    if (event.source !== 'product-form') return;

    recordAddedProduct(handleFromAddResponse(event.cartData));
  });
})();
